var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CopyAll, Edit, FileCopy, IosShare, Update, UpdateDisabled, } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, IconButton, ListItemText, Menu, MenuItem, Stack, Typography, } from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { BackgroundSelector } from '../components/BackgroundSelector';
import { SearchBar } from '../components/SearchBar';
import { DEFAULT_FILTER_CONFIGURATION, FilterConfigurationSpec, } from '../parsing/UiTypesSpec';
import { useAlertStore } from '../store/alerts';
import { useFilterConfigStore } from '../store/filterConfigurationStore';
import { useFilterStore } from '../store/filterStore';
import { useOboardingStore } from '../store/onboarding';
import { colors } from '../styles/MuiTheme';
import { countConfigChanges } from '../utils/configUtils';
import { downloadFile } from '../utils/file';
import { generateId } from '../utils/idgen';
import { createLink } from '../utils/link';
import { loadFilterFromUrl } from '../utils/loaderv2';
import { renderFilter } from '../utils/render';
import { UISelect } from './inputs/UISelect';
import { SmartTooltip } from './SmartTooltip';
import ImportRuneliteGif from '../images/import_runelite.gif';
import { toThemeStructuredComment } from '../utils/filterTheme';
import { FeatureFlagged } from './FeatureFlagged';
var updateAvailableFn = function (activeFilter, updatedFilter) {
    try {
        return ((updatedFilter === null || updatedFilter === void 0 ? void 0 : updatedFilter.rs2fHash) !== undefined &&
            (activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.rs2fHash) !== (updatedFilter === null || updatedFilter === void 0 ? void 0 : updatedFilter.rs2fHash));
    }
    catch (e) {
        console.log('error', e);
        return false;
    }
};
var isAutoUpdate = function (filterUrl) {
    return filterUrl != null &&
        filterUrl != undefined &&
        ((filterUrl === null || filterUrl === void 0 ? void 0 : filterUrl.startsWith('https://raw.githubusercontent.com/riktenx/filterscape')) ||
            (filterUrl === null || filterUrl === void 0 ? void 0 : filterUrl.startsWith('https://raw.githubusercontent.com/typical-whack/loot-filters-modules')) ||
            (filterUrl === null || filterUrl === void 0 ? void 0 : filterUrl.startsWith('https://raw.githubusercontent.com/Storn42/')));
};
var UpdateAvailableDialog = function (_a) {
    var open = _a.open, filterName = _a.filterName, filterUrl = _a.filterUrl, onClose = _a.onClose, onUpdate = _a.onUpdate;
    var updateText = (_jsx("span", { children: "A new version of the filter is available. Would you like to update?" }));
    var autoUpdate = isAutoUpdate(filterUrl);
    var _b = useState(false), timeoutDone = _b[0], setTimeoutDone = _b[1];
    if (autoUpdate) {
        updateText = (_jsxs("span", { children: ["This filter is supported by the loot-filters plugin or filterscape site maintainers. To keep it compatible with the site it is being automatically updated.", _jsx("br", {}), _jsx("br", {}), "After the update re-import the filter to RuneLite to apply the changes."] }));
        setTimeout(function () {
            setTimeoutDone(true);
        }, 3000);
    }
    return (
    // Do not set onClose to prevent closing with escape key & clicking away
    // We want to force the user to click the button to close the dialog
    _jsxs(Dialog, __assign({ open: open }, { children: [_jsxs(DialogTitle, __assign({ sx: { textAlign: 'center' } }, { children: ["Update Available for ", filterName] })), _jsx(DialogContent, { children: _jsx(Typography, __assign({ sx: { textAlign: 'center', color: colors.rsLightestBrown } }, { children: updateText })) }), _jsxs(DialogActions, __assign({ sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                } }, { children: [autoUpdate && !timeoutDone && (_jsxs(Typography, { children: ["Update in progress...", _jsx(CircularProgress, { size: 16 })] })), autoUpdate && timeoutDone && (_jsx(Button, __assign({ variant: "outlined", onClick: onUpdate }, { children: "I will ReImport to RuneLite" }))), !autoUpdate && (_jsxs("div", __assign({ style: { display: 'flex', gap: 10 } }, { children: [_jsx(Button, __assign({ sx: {
                                    borderColor: colors.rsLightestBrown,
                                    color: colors.rsLightestBrown,
                                }, variant: "outlined", onClick: onClose }, { children: "Cancel" })), _jsx(Button, __assign({ sx: {
                                    color: colors.rsYellow,
                                    borderColor: colors.rsYellow,
                                }, variant: "outlined", onClick: onUpdate }, { children: "Update" }))] })))] }))] })));
};
export var FilterSelector = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var reloadOnChange = _a.reloadOnChange;
    var navigate = useNavigate();
    var _j = useOboardingStore(), disableExportDialog = _j.disableExportDialog, setDisableExportDialog = _j.setDisableExportDialog;
    var _k = useFilterStore(), filters = _k.filters, removeFilter = _k.removeFilter, setActiveFilter = _k.setActiveFilter, updateFilter = _k.updateFilter, hasHydrated = _k.hasHydrated;
    var _l = useFilterConfigStore(), setFilterConfiguration = _l.setFilterConfiguration, removeFilterConfiguration = _l.removeFilterConfiguration;
    var _m = useState(false), deleteDialogOpen = _m[0], setDeleteDialogOpen = _m[1];
    var _o = useState(false), exportDialogOpen = _o[0], setExportDialogOpen = _o[1];
    var _p = useState(false), exportThemeDialogOpen = _p[0], setExportThemeDialogOpen = _p[1];
    var activeFilter = useMemo(function () { return Object.values(filters).find(function (filter) { return filter.active; }); }, [filters]);
    var activeFilterConfig = FilterConfigurationSpec.optional().parse(useFilterConfigStore(function (state) { var _a; return activeFilter && ((_a = state.filterConfigurations) === null || _a === void 0 ? void 0 : _a[activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.id]); }));
    var _q = useState(null), updatedFilter = _q[0], setUpdatedFilter = _q[1];
    var handleFilterChange = useCallback(function (newValue) {
        if (newValue) {
            setUpdatedFilter(null);
            setActiveFilter(newValue.value);
            if (reloadOnChange) {
                window.location.reload();
            }
        }
    }, [setActiveFilter]);
    var handleDeleteFilter = useCallback(function () {
        if (activeFilter) {
            removeFilter(activeFilter.id);
            removeFilterConfiguration(activeFilter.id);
        }
    }, [activeFilter, setActiveFilter, removeFilter, removeFilterConfiguration]);
    var filterOptions = Object.values(filters).map(function (filter) { return ({
        label: filter.name,
        value: filter.id,
    }); });
    var selectedFilter = activeFilter
        ? {
            label: activeFilter.name,
            value: activeFilter.id,
        }
        : null;
    var addAlert = useAlertStore(function (state) { return state.addAlert; });
    var updateAvailable = useMemo(function () {
        var result = updateAvailableFn(activeFilter, updatedFilter);
        return result;
    }, [activeFilter, updatedFilter]);
    var _r = useState(false), creatingLink = _r[0], setCreatingLink = _r[1];
    useEffect(function () {
        if (activeFilter != null &&
            activeFilter.source != null &&
            updatedFilter == null) {
            loadFilterFromUrl(activeFilter.source)
                .then(function (newFilter) {
                setUpdatedFilter(newFilter);
            })
                .catch(function (e) {
                addAlert({
                    children: "Failed to check for updates to filter: ".concat(e.message),
                    severity: 'error',
                });
            });
        }
    }, [activeFilter]);
    var _s = useState(null), filterMenuAnchor = _s[0], setFilterMenuAnchor = _s[1];
    var copyToClipboardButton = (_jsx(SmartTooltip, __assign({ enabledTitle: "Copy filter to clipboard", disabledTitle: "No filter selected", enabled: activeFilter != null }, { children: _jsxs(Button, __assign({ variant: "main-action", disabled: !activeFilter, onClick: function () {
                if (!activeFilter) {
                    return;
                }
                var renderedFilter = renderFilter(activeFilter, activeFilterConfig);
                navigator.clipboard
                    .writeText(renderedFilter)
                    .then(function () {
                    if (disableExportDialog) {
                        addAlert({
                            children: 'Filter copied to clipboard, import it to RuneLite via the Loot Filters plugin panel.',
                            severity: 'success',
                        });
                    }
                    else {
                        setExportDialogOpen(true);
                    }
                })
                    .catch(function () {
                    addAlert({
                        children: 'Failed to copy filter to clipboard',
                        severity: 'error',
                    });
                });
            } }, { children: [_jsx(ContentCopyIcon, { sx: { fontSize: '20px', paddingRight: '5px' } }), "Export to RuneLite"] })) })));
    var updateFilterFn = function () {
        if (!activeFilter) {
            return;
        }
        if (updatedFilter != null) {
            var updatedAt = new Date().toISOString();
            updateFilter(__assign(__assign({}, updatedFilter), { id: activeFilter.id, name: activeFilter.name, importedOn: activeFilter.importedOn, updatedOn: updatedAt, active: true, description: updatedFilter === null || updatedFilter === void 0 ? void 0 : updatedFilter.description }));
            setUpdatedFilter(null);
            addAlert({
                children: 'Filter updated',
                severity: 'success',
            });
        }
    };
    var updateFilterButton = (_jsx(SmartTooltip, __assign({ enabledTitle: "Update filter", disabledTitle: "No updates available", enabled: updateAvailable }, { children: _jsxs(IconButton, __assign({ disabled: !updateAvailable, onClick: updateFilterFn }, { children: [!updateAvailable && (_jsx(UpdateDisabled, { style: { color: 'grey' } })), updateAvailable && (_jsx(Update, { style: {
                        color: colors.rsOrange,
                    } }))] })) })));
    var shareFilterButton = (_jsx(SmartTooltip, __assign({ enabledTitle: "Share filter link", disabledTitle: creatingLink
            ? 'Creating link...'
            : 'Filters without a source URL cannot be shared', enabled: (activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.source) != null && !creatingLink }, { children: _jsx("span", { children: _jsxs(Button, __assign({ variant: "outlined", disabled: !activeFilter || !activeFilter.source || creatingLink, onClick: function () {
                    if (!activeFilter) {
                        return;
                    }
                    if (activeFilter.source === null) {
                        addAlert({
                            children: 'Filters without a source URL cannot be shared',
                            severity: 'error',
                        });
                        return;
                    }
                    setCreatingLink(true);
                    createLink(activeFilter, activeFilterConfig)
                        .then(function (link) {
                        return navigator.clipboard
                            .writeText(link)
                            .then(function () {
                            addAlert({
                                children: 'Filter link copied to clipboard',
                                severity: 'success',
                            });
                        });
                    })
                        .finally(function () {
                        setCreatingLink(false);
                    })
                        .catch(function (error) {
                        setCreatingLink(false);
                        console.error(error);
                        addAlert({
                            children: "Failed to copy filter link to clipboard: ".concat(error instanceof Error ? error.message : 'Unknown error'),
                            severity: 'error',
                        });
                    });
                } }, { children: [_jsx(IosShare, { style: {
                            fontSize: '20px',
                            paddingRight: '5px',
                            color: (activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.source)
                                ? colors.rsOrange
                                : colors.rsGrey,
                        } }), "Share"] })) }) })));
    var menuButton = (_jsx(IconButton, __assign({ onClick: function (e) { return setFilterMenuAnchor(e.currentTarget); } }, { children: _jsx(MoreVertIcon, { style: {
                color: colors.rsDarkOrange,
            } }) })));
    var menu = (_jsxs(Menu, __assign({ open: filterMenuAnchor != null, onClose: function () { return setFilterMenuAnchor(null); }, anchorEl: filterMenuAnchor }, { children: [_jsx(SmartTooltip, __assign({ enabledTitle: "Edit filter", disabledTitle: "No filter selected", enabled: activeFilter != null, tooltipSide: "right" }, { children: _jsxs(MenuItem, __assign({ disabled: !activeFilter, onClick: function () {
                        navigate("/editor/".concat(activeFilter.id));
                    } }, { children: [_jsx(ListItemIcon, { children: _jsx(Edit, {}) }), _jsx(ListItemText, { children: "Edit Filter" })] })) })), _jsx(SmartTooltip, __assign({ enabledTitle: "Download filter", disabledTitle: "No filter selected", enabled: activeFilter != null, tooltipSide: "right" }, { children: _jsxs(MenuItem, __assign({ disabled: !activeFilter, onClick: function () {
                        if (!activeFilter) {
                            return;
                        }
                        var renderedFilter = renderFilter(activeFilter, activeFilterConfig);
                        var fileName = "".concat(activeFilter.name, ".rs2f");
                        var file = new File([renderedFilter], fileName, {
                            type: 'text/plain',
                        });
                        downloadFile(file);
                    } }, { children: [_jsx(ListItemIcon, { children: _jsx(DownloadIcon, { fontSize: "small" }) }), _jsx(ListItemText, { children: "Download" })] })) })), _jsx(SmartTooltip, __assign({ enabledTitle: "Duplicate filter with current settings", disabledTitle: "No filter selected", enabled: activeFilter != null, tooltipSide: "right" }, { children: _jsxs(MenuItem, __assign({ disabled: !activeFilter, onClick: function () {
                        if (!activeFilter) {
                            return;
                        }
                        var newId = generateId();
                        setFilterConfiguration(newId, activeFilterConfig || DEFAULT_FILTER_CONFIGURATION);
                        updateFilter(__assign(__assign({}, activeFilter), { id: newId, name: "".concat(activeFilter.name, " (copy)") }));
                        setActiveFilter(newId);
                        addAlert({
                            children: 'Filter duplicated',
                            severity: 'success',
                        });
                    } }, { children: [_jsx(ListItemIcon, { children: _jsx(FileCopy, { fontSize: "small" }) }), _jsx(ListItemText, { children: "Duplicate" })] })) })), _jsx(SmartTooltip, __assign({ enabledTitle: "Delete filter", disabledTitle: "No filter selected", enabled: activeFilter != null, tooltipSide: "right" }, { children: _jsxs(MenuItem, __assign({ disabled: !activeFilter, onClick: function () { return setDeleteDialogOpen(true); } }, { children: [_jsx(ListItemIcon, { children: _jsx(DeleteIcon, { style: { color: colors.rsRed }, fontSize: "small" }) }), _jsx(ListItemText, { children: "Delete" })] })) })), _jsx(FeatureFlagged, __assign({ featureFlag: "themes" }, { children: _jsxs(MenuItem, __assign({ disabled: !activeFilter, onClick: function () { return setExportThemeDialogOpen(true); } }, { children: [_jsx(ListItemIcon, { children: _jsx(CopyAll, { fontSize: "small" }) }), _jsx(ListItemText, { children: "Copy configuration as Theme" })] })) }))] })));
    var themeSelector = (_jsx(FeatureFlagged, __assign({ featureFlag: "themes" }, { children: _jsx(UISelect, { label: "Theme", sx: { width: '200px' }, options: __spreadArray([
                { label: 'Default', value: '' }
            ], ((_c = (_b = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.themes) === null || _b === void 0 ? void 0 : _b.map(function (theme) { return ({
                label: theme.name,
                value: theme.id,
            }); })) !== null && _c !== void 0 ? _c : []), true), value: {
                label: ((_e = (_d = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.themes) === null || _d === void 0 ? void 0 : _d.find(function (theme) {
                    return theme.id === (activeFilterConfig === null || activeFilterConfig === void 0 ? void 0 : activeFilterConfig.selectedThemeId);
                })) === null || _e === void 0 ? void 0 : _e.name) || 'Default',
                value: (activeFilterConfig === null || activeFilterConfig === void 0 ? void 0 : activeFilterConfig.selectedThemeId) || '',
            }, onChange: function (value) {
                if (value == null) {
                    return;
                }
                setFilterConfiguration(activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.id, __assign(__assign({}, activeFilterConfig), { selectedThemeId: (value === null || value === void 0 ? void 0 : value.value) === '' ? undefined : value === null || value === void 0 ? void 0 : value.value }));
            } }) })));
    if (!hasHydrated) {
        return null;
    }
    if (Object.keys(filters).length === 0) {
        return _jsx(Navigate, { to: "/new-filter" });
    }
    return (_jsxs(_Fragment, { children: [_jsx(Stack, __assign({ spacing: 2 }, { children: _jsxs(Box, __assign({ sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginLeft: 'auto',
                    } }, { children: [_jsx(FormControl, __assign({ sx: {
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                            }, size: "small" }, { children: _jsx(UISelect, { sx: { width: '300px' }, options: filterOptions, value: selectedFilter, onChange: handleFilterChange, label: "Select a filter", disabled: Object.keys(filters).length === 0, multiple: false }) })), themeSelector, copyToClipboardButton, !isAutoUpdate(activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.source) && updateFilterButton, shareFilterButton, menuButton, menu, _jsx(UpdateAvailableDialog, { open: updateAvailable, filterName: (_f = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.name) !== null && _f !== void 0 ? _f : '', filterUrl: (_g = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.source) !== null && _g !== void 0 ? _g : '', onClose: function () {
                                setUpdatedFilter(null);
                            }, onUpdate: function () {
                                updateFilterFn();
                                setUpdatedFilter(null);
                            } }), _jsx("div", __assign({ style: { marginLeft: 'auto' } }, { children: _jsx(SearchBar, {}) })), _jsx(BackgroundSelector, {})] })) })), _jsxs(Dialog, __assign({ open: deleteDialogOpen, onClose: function () { return setDeleteDialogOpen(false); } }, { children: [_jsx(DialogTitle, { children: "Delete Filter?" }), _jsxs(DialogContent, { children: [_jsxs(Typography, __assign({ sx: {
                                    fontSize: '3rem',
                                    color: colors.rsLightestBrown,
                                } }, { children: ["Filter ", activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.name, " has", ' ', _jsx("span", __assign({ style: { color: colors.rsYellow } }, { children: countConfigChanges((_h = activeFilterConfig) !== null && _h !== void 0 ? _h : {}) })), ' ', "configuration changes."] })), _jsx(DialogContentText, __assign({ sx: {
                                    fontSize: '2rem',
                                    color: colors.rsRed,
                                } }, { children: "This action cannot be undone." }))] }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: function () { return setDeleteDialogOpen(false); } }, { children: "Cancel" })), _jsx(Button, __assign({ variant: "outlined", style: { color: colors.rsRed }, onClick: handleDeleteFilter }, { children: "Delete" }))] })] })), _jsxs(Dialog, __assign({ open: exportDialogOpen, onClose: function () { return setExportDialogOpen(false); } }, { children: [_jsx(DialogTitle, { children: "The filter has been copied to the clipboard." }), _jsxs(DialogContent, { children: [_jsx(Typography, __assign({ color: colors.rsLightestBrown }, { children: "Navigate to the Loot Filters plugin panel in RuneLite and click the \"Import from clipboard\" button:" })), _jsx("div", __assign({ style: { display: 'flex', justifyContent: 'center' } }, { children: _jsx("img", { src: ImportRuneliteGif }) }))] }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: function () { return setExportDialogOpen(false); } }, { children: "OK" })), _jsx(Button, __assign({ variant: "outlined", onClick: function () {
                                    setDisableExportDialog(true);
                                    setExportDialogOpen(false);
                                } }, { children: "Don't show this again" }))] })] })), _jsxs(Dialog, __assign({ open: exportThemeDialogOpen, onClose: function () { return setExportThemeDialogOpen(false); } }, { children: [_jsx(DialogTitle, { children: "Export Theme" }), _jsx(DialogContent, { children: _jsx("pre", { children: JSON.stringify(activeFilterConfig, null, 2) }) }), _jsx(DialogActions, { children: _jsx(Button, __assign({ variant: "outlined", onClick: function () {
                                setExportThemeDialogOpen(false);
                                navigator.clipboard.writeText(toThemeStructuredComment('test', activeFilterConfig, 'subtitle', 'description'));
                            } }, { children: "Copy to clipboard" })) })] }))] }));
};
